import { SCol, SIcon, SPagination, SRow } from "@avalara/skylab-sdk/react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { getStatesAsync } from "../../app/commonSlice";
import {
    clearCertDetail,
    EcmsRecord,
    fetchEcms,
    fetchEcmsCertificateDetail,
    revokeEcmsCert,
    selectEcms,
    selectEcmsCertDetail,
    selectEcmsError,
    selectEcmsIsLoading,
    selectHasFilterChips,
    selectSortColumn,
    setSortColumn,
} from "../../app/ecmsSlice";
import { selectSession, updateRowCountPreference } from "../../app/sessionSlice";
import { SessionState } from "../../app/sessionSlice/types";
import { AppDispatch } from "../../app/types";
import toast from "../../hooks/toast";
import { useGridScroll } from "../../hooks/useGridScroll";
import DataGrid from "../../shared/dataGrid/DataGrid";
import { GridHeader } from "../../shared/dataGrid/types";
import { getPageTitle } from "../../shared/sessionUtility";
import { getRowsPerPage } from "../../shared/Utils";
import "../search/shared-grid-sticky.css";
import Loader from "../sharedComponents/Loader";
import "./ecms.css";
import ECMSFilter from "./ECMSFilter";
import EcmsCertificateDetailDialog from "./EcmsCertificateDetailDialog";
import EcmsCustomerDetailDialog from "./EcmsCustomerDetailDialog";

// Types
interface PaginationEvent {
    detail: {
        currentPage: number;
        startIndex: number;
        endIndex: number;
        rowsPerPage: number;
        flag: string;
    };
}

/**
 * The AvaTax ECMS API can return several exempt-certificate entries (different exemptCertId,
 * status, regions, etc.) for the same CertCapture certificate (avaCertId). Those are grouped
 * into a single grid row so the identifying customer/cert info isn't repeated per entry.
 */
interface EcmsGroupedRow {
    id: string;
    customerName: string;
    customerCode: string;
    avaCertId: string;
    country: string;
    entries: EcmsRecord[];
}

const isActiveEcmsStatus = (status: string | undefined): boolean =>
    (status ?? "").toLowerCase() === "active";

const getEcmsRegionsDisplay = (entry: EcmsRecord): string => {
    const value = entry.regionsApplicable ?? entry.region;
    return value?.split(",")?.join(", ") ?? "";
};

const getEcmsEntryField = (entry: EcmsRecord, field: string): string =>
    (entry as unknown as Record<string, string | undefined>)[field] ?? "";

const groupEcmsRecordsByCertId = (records: EcmsRecord[]): EcmsGroupedRow[] => {
    const groups = new Map<string, EcmsGroupedRow>();
    records.forEach(record => {
        const key = record.avaCertId;
        const existing = groups.get(key);
        if (existing) {
            existing.entries.push(record);
        } else {
            groups.set(key, {
                id: key,
                customerName: record.customerName,
                customerCode: record.customerCode,
                avaCertId: record.avaCertId,
                country: record.country,
                entries: [record],
            });
        }
    });
    return [...groups.values()];
};

function ECMS(): React.JSX.Element {
    const dispatch = useDispatch<AppDispatch>();
    const ecms = useSelector(selectEcms);
    const ecmsData: EcmsRecord[] = ecms.data;
    const groupedEcmsData: EcmsGroupedRow[] = groupEcmsRecordsByCertId(ecmsData ?? []);
    const paginateData = ecms.paginate;
    const isLoading = useSelector(selectEcmsIsLoading);
    const error = useSelector(selectEcmsError);
    const { rowsPerPage } = useSelector(selectSession, shallowEqual) as SessionState;
    const [pageTitle, setPageTitle] = useState<string>("ECMS");
    const sortColumn = useSelector(selectSortColumn);
    const hasFilterChips = useSelector(selectHasFilterChips);
    const [openFilters, setOpenFilters] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<EcmsRecord | null>(null);
    const [selectedCertRecord, setSelectedCertRecord] = useState<EcmsRecord | null>(null);
    const certDetail = useSelector(selectEcmsCertDetail);
    const [showToast] = toast();

    // `paginate.rowsPerPage` is the size the loaded page was actually fetched with, while
    // `session.rowsPerPage` only catches up once updateRowCountPreference's POST + GET returns
    // (and stays stale if either call fails). Derive the size once so the pagination control and
    // every refetch agree; fall back to the session preference before the first page loads.
    // `startIndex >= 0` is the "a page has loaded" test (initialState uses -1 as the sentinel):
    // the fetched size is just as real on a response that returned no rows, so keying off
    // totalRecords would drop back to the stale preference after a filter that matches nothing.
    const effectiveRowsPerPage: number =
        paginateData?.startIndex >= 0 ? paginateData.rowsPerPage : rowsPerPage;

    // Open the certificate-details dialog for a row and fetch its full detail (jurisdiction /
    // jobs arrays the list call omits). The header/summary paint instantly from the seed row.
    const handleOpenCertDetail = (data: EcmsRecord): void => {
        setSelectedCertRecord(data);
        dispatch(
            fetchEcmsCertificateDetail({
                exemptCertId: data.exemptCertId,
                companyId: data.companyId,
            })
        );
    };

    const handleCloseCertDetail = (): void => {
        setSelectedCertRecord(null);
        dispatch(clearCertDetail());
    };

    const { handleScroll: handleGridScroll } = useGridScroll({
        wrapperClass: "ecms-grid-scrollable",
        trackLeftScroll: true,
        trackRightScroll: false,
        hasData: groupedEcmsData.length > 0,
        isLoading,
        dependencies: [groupedEcmsData.length, isLoading],
    });

    const toggleFilters = (): void => {
        setOpenFilters(!openFilters);
    };

    useEffect(() => {
        if (isLoading) return;
        // Don't re-fire after a failed load, otherwise a 4xx/5xx retries on every render.
        if (error) return;
        if ((ecmsData?.length ?? 0) > 0) return;
        if (paginateData?.startIndex >= 0) return;
        dispatch(fetchEcms());
        // Fetch states for region filter - includes US and Canada states
        dispatch(getStatesAsync(false));
    }, [dispatch, isLoading, error, ecmsData?.length, paginateData?.startIndex]);

    useEffect(() => {
        const title = dispatch(getPageTitle("ECMS")) as unknown;
        if (typeof title === "string") {
            setPageTitle(title);
        }
    }, [dispatch]);

    // Show error toast when API fails
    useEffect(() => {
        if (error) {
            showToast("error", `Failed to load ECMS data: ${error}`, "ecms-error-toast");
        }
    }, [error, showToast]);

    const handlePaginate = (e: PaginationEvent): void => {
        if (e.detail.currentPage !== 0 && paginateData?.totalRecords > 0 && e.detail.flag !== "") {
            if (e.detail.rowsPerPage !== rowsPerPage) {
                dispatch(updateRowCountPreference(e.detail.rowsPerPage));
            }
            dispatch(
                // Omit filterString so fetchEcms rebuilds it from the active filters state;
                // passing "" here would drop any applied filters when changing pages.
                fetchEcms({
                    top: e?.detail?.rowsPerPage,
                    skip: e?.detail?.startIndex,
                    paginateDetails: {
                        pageNumber: e?.detail?.currentPage,
                        startIndex: e?.detail?.startIndex,
                        endIndex: e?.detail?.endIndex,
                        rowsPerPage: e?.detail?.rowsPerPage,
                    },
                })
            );
        }
    };

    const sortFunction = (columnId: string): void => {
        const order = sortColumn[0] === columnId ? !sortColumn[1] : true;
        dispatch(setSortColumn([columnId, order]));
        dispatch(
            // Omit filterString so active filters are preserved when re-sorting; sorting
            // resets to the first page (skip: 0).
            fetchEcms({
                top: effectiveRowsPerPage,
                skip: 0,
            })
        );
    };

    const handleEcmsCertificateRevocation = async (ecmsCertId: string): Promise<void> => {
        if (!ecmsCertId) return;
        try {
            await dispatch(
                revokeEcmsCert({
                    ecmsCertId,
                    auditMessage: "ECM Certificate Revoke",
                })
            ).unwrap();
            showToast("success", "ECMS certificate revoked successfully", "ecms-success-toast");
        } catch {
            showToast("error", "Failed to revoke ECMS certificate", "ecms-error-toast");
        }
        dispatch(fetchEcms());
    };

    const renderEcmsEntryRows = (
        row: EcmsGroupedRow,
        cell: (entry: EcmsRecord) => React.ReactNode
    ): React.ReactNode => (
        <div className="ecms-subrow-stack flex flex-dir-col">
            {row.entries.map(entry => (
                <div className="ecms-subrow flex align-items-center" key={entry.id}>
                    {cell(entry)}
                </div>
            ))}
        </div>
    );

    const gridHeaders: GridHeader<EcmsGroupedRow>[] = [
        { id: "customerName", label: "Customer Name", sortable: true },
        {
            id: "customerCode",
            label: "Customer Code",
            sortable: true,
            render: (data: EcmsGroupedRow) => {
                const customerDetails = data.entries[0];
                return (
                    <button
                        type="button"
                        className="link"
                        onClick={() => setSelectedRecord(customerDetails)}
                        data-testid={`${testIDs.ecms.customerCodeLink}-${customerDetails.exemptCertId}`}>
                        {data.customerCode}
                    </button>
                );
            },
        },
        { id: "avaCertId", label: "Cert ID", sortable: true },
        { id: "country", label: "Country" },
        {
            id: "exemptCertId",
            label: "Ecms Cert ID",
            // Sortable so the default sort (exemptCertId DESC) shows an active header and can be
            // toggled back to after sorting another column.
            sortable: true,
            render: row =>
                renderEcmsEntryRows(row, entry => (
                    <button
                        type="button"
                        className="link"
                        onClick={() => handleOpenCertDetail(entry)}
                        data-testid={`${testIDs.ecms.certIdLink}-${entry.exemptCertId}`}>
                        {entry.exemptCertId}
                    </button>
                )),
        },
        {
            id: "regionsApplicable",
            label: "Regions",
            render: row => renderEcmsEntryRows(row, entry => getEcmsRegionsDisplay(entry)),
        },
        {
            id: "exemptReasonDescription",
            label: "Exemption Reason",
            render: row =>
                renderEcmsEntryRows(row, entry =>
                    getEcmsEntryField(entry, "exemptReasonDescription")
                ),
        },
        {
            id: "businessTypeDescription",
            label: "Business Type",
            render: row =>
                renderEcmsEntryRows(row, entry =>
                    getEcmsEntryField(entry, "businessTypeDescription")
                ),
        },
        {
            id: "exemptCertStatusId",
            label: "Status",
            render: row => renderEcmsEntryRows(row, entry => entry.exemptCertStatusId),
        },
        {
            id: "actions",
            label: null,
            render: row =>
                renderEcmsEntryRows(row, entry =>
                    isActiveEcmsStatus(entry.exemptCertStatusId) ? (
                        <button
                            type="button"
                            className="secondary"
                            onClick={() => handleEcmsCertificateRevocation(entry.exemptCertId)}
                            data-testid={`${testIDs.ecms.grid}-revoke-button-${entry.exemptCertId}`}>
                            Revoke
                        </button>
                    ) : null
                ),
        },
    ];

    let pagination: React.JSX.Element | null = null;
    if (paginateData?.totalRecords > 0) {
        pagination = (
            <SPagination
                className="margin-top-md"
                rowsPerPage={getRowsPerPage(paginateData, effectiveRowsPerPage) as number}
                totalRecords={paginateData?.totalRecords}
                startIndex={paginateData?.startIndex}
                // The ECMS service sends no record count, so totalRecords is an estimate for
                // those responses. Indeterminate hides the "x-y of z" total and the last-page
                // jump, keeping only what an unknown total can honestly support.
                indeterminate={paginateData?.indeterminate || undefined}
                onS-paginate={(e: PaginationEvent) => {
                    handlePaginate(e);
                }}
            />
        );
    }

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <h1>ECMS</h1>
            {!isLoading && (
                <button
                    type="button"
                    className="secondary margin-bottom-md ecms-filters-button"
                    onClick={toggleFilters}
                    data-testid={testIDs.ecms.filtersButton}>
                    <SIcon name="filter" className="margin-right-xs" aria-hidden="true" />
                    Filters
                </button>
            )}
            <SRow>
                <SCol span="12">
                    <ECMSFilter
                        hasFilterChips={hasFilterChips}
                        openFilters={openFilters}
                        onToggleFilters={toggleFilters}
                        rowsPerPage={effectiveRowsPerPage}
                    />
                    {isLoading ? (
                        <Loader loading={isLoading} />
                    ) : (
                        <>
                            <DataGrid
                                gridHeaders={gridHeaders}
                                gridData={groupedEcmsData}
                                sortFunction={sortFunction}
                                gridName="records"
                                totalRecords={paginateData?.totalRecords}
                                sortColumn={sortColumn}
                                hasFilterChips={hasFilterChips}
                                usesFilterChips
                                scrollable
                                scrollableClassName="ecms-grid-scrollable"
                                onScroll={handleGridScroll}
                                errorMessage={
                                    error
                                        ? "Unable to load ECMS data. Please try again or contact support if the issue persists."
                                        : undefined
                                }
                                testId={testIDs.ecms.grid}
                            />
                            {pagination}
                        </>
                    )}
                </SCol>
            </SRow>
            <EcmsCustomerDetailDialog
                record={selectedRecord}
                open={selectedRecord !== null}
                onClose={() => setSelectedRecord(null)}
            />
            <EcmsCertificateDetailDialog
                record={selectedCertRecord}
                detail={certDetail.record}
                loading={certDetail.isLoading}
                error={certDetail.error}
                open={selectedCertRecord !== null}
                onClose={handleCloseCertDetail}
            />
        </>
    );
}

export default ECMS;
